import { Question } from '../question/question';
import { DistributionTerminalsAssistantModel, DistributionTerminalsAssistantRequirement, isRequirement, mapPotentialToColor, potentials } from './distribution-terminals-assistant.model';

export type DistributionTerminalAssistantQuestion = Question & {
  category: string,
  topic: string,
  defaultAnswer?: any,
};

export type Summary = Record<string, SummaryValue[]>;

export type SummaryValue = {
  name: string;
  value: string;
  unitText?: string;
  translateValue?: boolean;
};

export class DistributionTerminalAssistantQuestions {
  private _currentIndex = 0;
  private _model = new DistributionTerminalsAssistantModel();
  private _questions: DistributionTerminalAssistantQuestion[] = [
    {
      type: "NumberValue",
      category: "configuration.assistants.distributionTerminals.question.category.supply",
      topic: "configuration.assistants.distributionTerminals.question.topic.totalCurrent",
      text: "configuration.assistants.distributionTerminals.question.feed.totalCurrent",
      minValue: 1,
      maxValue: 90,
      unit: "global.unit.label.ampere",
      unitText: "configuration.assistants.distributionTerminals.question.feed.totalCurrent.unitText",
      submitAnswer: (answer) => (this._model.supply.totalCurrent = answer),
    },
    {
      type: "ColorMultipleChoice",
      category: "configuration.assistants.distributionTerminals.question.category.supply",
      topic: "configuration.assistants.distributionTerminals.question.topic.potential",
      text: "configuration.assistants.distributionTerminals.question.feed.potential",
      answers: () => potentials.map((potential) => ({
        value: potential,
        metadata: {
          text: potential,
          color: mapPotentialToColor(potential),
          translateText: false,
        }
      })),
      submitAnswer: (answer) => (this._model.potential = answer),
    },
    {
      type: "MultipleChoice",
      category: "configuration.assistants.distributionTerminals.question.category.supply",
      topic: "configuration.assistants.distributionTerminals.question.topic.crossSection",
      text: "configuration.assistants.distributionTerminals.question.feed.crossSection",
      answers: () => this._model.supply.crossSectionValues.map((crossSection) => ({
        value: crossSection,
        metadata: {
          text: `${crossSection} mm²`,
          translateText: false,
        }
      })),
      submitAnswer: (answer) => (this._model.supply.crossSection = answer),
    },
    {
      type: "ImageMultipleChoice",
      category: "configuration.assistants.distributionTerminals.question.category.supply",
      topic: "configuration.assistants.distributionTerminals.question.topic.actuationType",
      text: "configuration.assistants.distributionTerminals.question.feed.actuationType",
      answers: () => this._model.supply.actuationTypeValues.map((actuationType) => ({
        value: actuationType,
        metadata: {
          text: `configuration.assistants.distributionTerminals.question.actuationType.${actuationType.toLowerCase().replace(' ', '-')}`,
          imageUrl: `ng-assets/img/assistants/distribution-terminals/distribution-terminals-${actuationType.toLowerCase().replace(' ', '-')}.webp`,
          translateText: true,
        }
      })),
      submitAnswer: (answer) => (this._model.supply.actuationType = answer),
    },
    {
      type: "MultipleChoice",
      category: "configuration.assistants.distributionTerminals.question.category.supply",
      topic: "configuration.assistants.distributionTerminals.question.topic.inputs",
      text: "configuration.assistants.distributionTerminals.question.feed.inputs",
      answers: () => this._model.supply.inputValues.map((input) => ({
        value: input,
        metadata: {
          text: `${input}`,
          translateText: false,
        }
      })),
      submitAnswer: (answer) => (this._model.supply.inputs = answer),
    },
    {
      type: "ImageMultipleChoice",
      category: "configuration.assistants.distributionTerminals.question.category.distribution",
      topic: "configuration.assistants.distributionTerminals.question.topic.actuationType",
      text: "configuration.assistants.distributionTerminals.question.distribution.actuationType",
      answers: () => this._model.distribution.actuationTypeValues.map((actuationType) => ({
        value: actuationType,
        metadata: {
          text: `configuration.assistants.distributionTerminals.question.actuationType.${actuationType.toLowerCase().replace(' ', '-')}`,
          imageUrl: `ng-assets/img/assistants/distribution-terminals/distribution-terminals-${actuationType.toLowerCase().replace(' ', '-')}.webp`,
          translateText: true,
        }
      })),
      submitAnswer: (answer) => (this._model.distribution.actuationType = answer),
    },
    {
      type: "MultipleChoice",
      category: "configuration.assistants.distributionTerminals.question.category.distribution",
      topic: "configuration.assistants.distributionTerminals.question.topic.crossSection",
      text: "configuration.assistants.distributionTerminals.question.distribution.crossSection",
      answers: () => this._model.distribution.crossSectionValues.map((crossSection) => ({
        value: crossSection,
        metadata: {
          text: `${crossSection} mm²`,
          translateText: false,
        }
      })),
      submitAnswer: (answer) => (this._model.distribution.crossSection = answer),
    },
    {
      type: "NumberValue",
      category: "configuration.assistants.distributionTerminals.question.category.distribution",
      topic: "configuration.assistants.distributionTerminals.question.topic.outputs",
      text: "configuration.assistants.distributionTerminals.question.distribution.outputs",
      minValue: 1,
      maxValue: 24,
      unitText: "",
      submitAnswer: (answer) => (this._model.distribution.outputs = answer),
    },
    {
      type: "ImageMultipleChoice",
      category: "configuration.assistants.distributionTerminals.question.category.distribution",
      topic: "configuration.assistants.distributionTerminals.question.topic.alignment",
      text: "configuration.assistants.distributionTerminals.question.distribution.alignment",
      answers: () => this._model.distribution.alignmentValues.map((alignment) => ({
        value: alignment,
        metadata: {
          text: `configuration.assistants.distributionTerminals.question.distribution.alignment.${alignment}`,
          imageUrl: `ng-assets/img/assistants/distribution-terminals/distribution-terminals-alignment-${alignment}.webp`,
          translateText: true,
        }
      })),
      submitAnswer: (answer) => (this._model.distribution.alignment = answer),
      defaultAnswer: 'symmetric',
    },
  ];

  public get list(): readonly DistributionTerminalAssistantQuestion[] {
    return this._questions;
  }

  public get length() {
    return this._questions.length;
  }

  public get currentQuestion(): DistributionTerminalAssistantQuestion {
    return this._questions[this._currentIndex];
  }

  public get currentIndex() {
    return this._currentIndex;
  }

  public next() {
    this.currentQuestion.submitAnswer(this.currentQuestion.answer);
    this._currentIndex++;
    if (this.currentQuestion && this.currentQuestion.type !== 'NumberValue') {
      if (this.currentQuestion.answers().length === 1) {
        this.currentQuestion.answer = this.currentQuestion.answers()[0].value;
        this.next();
      }
    }
    this.setDefaultAnswerIfAvailable(this.currentQuestion);
  }

  public back() {
    if (this.currentQuestion) {
      this.resetQuestion(this.currentQuestion);
      this._currentIndex--;
    } else {
      this._currentIndex = this._questions.length - 1;
    }
    if (
      (this.currentQuestion.type !== 'NumberValue') &&
      this.currentQuestion.answers().length === 1
    ) {
      this.back();
    }
  }

  private setDefaultAnswerIfAvailable(question: DistributionTerminalAssistantQuestion) {
    if (question?.defaultAnswer) {
      question.answer = question.defaultAnswer;
      question.submitAnswer(question.answer);
    }
  }

  private resetQuestion(question: DistributionTerminalAssistantQuestion) {
    question.submitAnswer(undefined);
    question.answer = undefined;
  }

  public getSummary(): Summary | undefined {
    if (isRequirement(this._model)) {
      return this._questions.reduce<Summary>(
        (summary: Summary, question: DistributionTerminalAssistantQuestion) => {
          if (!summary.hasOwnProperty(question.category)) {
            summary[question.category] = [];
          }
          summary[question.category].push(this.getSummaryValue(question));
          return summary;
        },
        {},
      );
    }
    return undefined;
  }

  private getSummaryValue(question: DistributionTerminalAssistantQuestion): SummaryValue {
    if (question.type !== 'NumberValue') {
      const metadata = question.answers().filter((answer) => answer.value === question.answer)[0].metadata;
      return {
        name: question.topic,
        value: metadata.text,
        translateValue: !metadata.text.startsWith(`${question.answer}`),
      };
    }
    return {
      name: question.topic,
      value: `${question.answer}`,
      unitText: question.unit,
      translateValue: false,
    };
  }

  public getResult(): DistributionTerminalsAssistantRequirement | undefined {
    if (isRequirement(this._model)) {
      return this._model;
    }
    return undefined;
  }
}
